import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import DashboardLayout from "../../layouts/Dashboard";
import ProtocolCRUDTable from "../../components/CRUD/Tables/ProtocolCRUDTable";
import {
  confirmProtocolWorkflow,
  protocolCRUD,
  rejectProtocolConfirmationWorkflow,
  searchProtocolsToConfirm,
} from "../../http";
import ProtocolDetails from "./Popups/ProtocolDetails";
import useAlert from "../../hooks/useAlert";
import { confirmDialog } from "../../components/Dialog/DialogDelete";
import { Button, Stack, TextField } from "@mui/material";
import {
  normalizeComplianceStatus,
  normalizeConfirmationStatus,
} from "../../models/protocolCompliance";

/**
 * Реестр «Протоколы к подтверждению»:
 * Signed + complianceStatus=1 + confirmationStatus=0.
 */
const ProtocolToConfirmList = () => {
  const { t } = useTranslation();
  const { reload, reloadValue } = useReload();
  const alert = useAlert(false, 1);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [comment, setComment] = useState("");

  const detailsHandler = async (id, listRow) => {
    try {
      const data = await protocolCRUD.get(id);
      const dataJson =
        typeof data?.data === "string"
          ? (() => {
              try {
                return JSON.parse(data.data);
              } catch {
                return {};
              }
            })()
          : data?.data || {};
      // GET иногда не отдаёт compliance/confirmation — берём из data / строки списка
      const complianceStatus =
        normalizeComplianceStatus(
          data?.complianceStatus ??
            data?.ComplianceStatus ??
            dataJson?.complianceStatus
        ) ??
        normalizeComplianceStatus(
          listRow?.complianceStatus ?? listRow?.ComplianceStatus
        );
      const confirmationStatus =
        normalizeConfirmationStatus(
          data?.confirmationStatus ??
            data?.ConfirmationStatus ??
            dataJson?.confirmationStatus
        ) ??
        normalizeConfirmationStatus(
          listRow?.confirmationStatus ?? listRow?.ConfirmationStatus
        );
      setProtocol({
        ...data,
        ...(complianceStatus != null ? { complianceStatus } : {}),
        ...(confirmationStatus != null ? { confirmationStatus } : {}),
      });
      setDetailsOpen(true);
    } catch (e) {
      alert.show(0, String(e));
    }
  };

  const handleConfirm = async (id) => {
    confirmDialog(
      "Подтверждение протокола",
      "Подтвердить протокол со статусом «Не соответствует»? После подтверждения протокол будет отправлен в ИС СЭН (eSen).",
      async () => {
        try {
          await confirmProtocolWorkflow(id, comment || undefined);
          setComment("");
          alert.show(1, "Протокол подтверждён и отправлен в ИС СЭН");
          reload();
        } catch (e) {
          alert.show(0, "Не удалось подтвердить: " + e);
        }
      }
    );
  };

  const handleReject = async (id) => {
    confirmDialog(
      "Отклонение подтверждения",
      "Отклонить протокол? В ИС СЭН (eSen) он отправлен не будет.",
      async () => {
        try {
          await rejectProtocolConfirmationWorkflow(id, comment || undefined);
          setComment("");
          alert.show(1, "Протокол отклонён — в ИС СЭН не отправляется");
          reload();
        } catch (e) {
          alert.show(0, "Не удалось отклонить: " + e);
        }
      }
    );
  };

  return (
    <DashboardLayout>
      <Stack direction="row" spacing={2} sx={{ mb: 2, px: 2, maxWidth: 480 }}>
        <TextField
          label="Комментарий к решению"
          size="small"
          fullWidth
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Stack>
      <ProtocolCRUDTable
        search={searchProtocolsToConfirm}
        onClickDetailsButton={detailsHandler}
        reloadValue={reloadValue}
        disableCreateButton
        showComplianceColumns
        extraActions={[
          {
            title: "Решение",
            key: "confirmationActions",
            fixed: "right",
            width: 220,
            render: (_, record) => (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleConfirm(record.id)}
                >
                  Подтвердить
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleReject(record.id)}
                >
                  Отклонить
                </Button>
              </Stack>
            ),
          },
        ]}
      />
      <ProtocolDetails
        action="details"
        title={t("protocolList.detailsText")}
        reload={reload}
        open={detailsOpen}
        setOpen={setDetailsOpen}
        protocol={protocol}
        allowConfirmation
      />
      {alert.render()}
    </DashboardLayout>
  );
};

export default ProtocolToConfirmList;
